import { MILLISECONDS } from '@chiroup/core/constants/time';
import { Condition } from '@chiroup/core/types/Condition.type';
import { use, useCallback, useMemo } from 'react';
import { QueryFunctionContext, useQuery, useQueryClient } from 'react-query';
import { MeContext } from '../../../contexts/me.context';
import { useSandboxClinicalData } from '../../../contexts/sandboxClinicalData.context';
import reportService from '../../../services/report.service';

const getAllConditions = (ids: number[], clinicId: number, userId: string, lightweight = false) => {
  return async (_context: QueryFunctionContext) => {
    return reportService.getConditionDetails(clinicId, userId, ids, false, lightweight);
  };
};

const useComplaintConditions = (ids: number[], lightweight = false) => {
  const sandbox = useSandboxClinicalData();
  const sortedIds = useMemo(() => [...ids].sort((a, b) => a - b), [ids]);
  const { me } = use(MeContext);
  const queryClient = useQueryClient();

  const { data, isFetching, refetch } = useQuery(
    ['complaintConditions', sortedIds, lightweight],
    sandbox
      ? () => ({ data: sandbox.conditions.filter(condition => sortedIds.includes(condition.ID)) })
      : getAllConditions(sortedIds, me.selectedClinic?.ID || -1, me.ID, lightweight),
    {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: MILLISECONDS.inMinutes(15),
    }
  );

  const allConditionsRetrieved: Record<number, Partial<Condition>> = data?.data?.reduce(
    (obj: Record<number, Partial<Condition>>, cond: Partial<Condition>) => {
      if (cond.ID) {
        obj[cond.ID] = cond;
      }

      return obj;
    },
    {} as Record<number, Partial<Condition>>
  ) || {};

  const updateClientData = useCallback(
    (id: number, updateData: any) => {
      queryClient.setQueryData(['complaintConditions', sortedIds, lightweight], (prev: any) => {
        const index = prev?.data?.findIndex(
          (item: any) => item.userVersion === id || item.clinicVersion === id || item.ID === id
        );

        if (!prev || index === -1 || index === undefined || !prev?.data[index]) {
          return prev;
        }

        prev.data[index] = { ...prev.data[index], ...updateData };

        return prev;
      });
    },
    [sortedIds, lightweight, queryClient]
  );

  return {
    data,
    isFetching,
    refetch,
    allConditionsRetrieved,
    updateClientData,
  };
};

export default useComplaintConditions;
